import React from "react";
import { useTranslation } from "react-i18next";
import { ThemeProvider } from "@greco/components";
import { Search } from "pages/BankStatements/BankStatementsGrid/Search";
import { ResultsScore } from "components/ResultsScore";
import { Flex, FlexRight } from "components/styled";
import { useAppStoreState } from "store/AppStore";
import { useImportedInvoicesStore } from "store/ImportedInvoicesStore";
import { ImportedInvoice, ImportedInvoiceEntry } from "types/importedInvoices";
import { helpAnchor } from "help/anchor";

// Bar between the master grid and the entry grid: search box for the entries of the
// selected invoice, the selected invoice's number and the number of visible entries.
const ImportedInvoiceEntriesSearchBar = () => {
  const { t } = useTranslation();
  const { appState } = useAppStoreState();
  const [
    {
      entriesSearchTerm,
      selectedImportedInvoiceId,
      importedInvoices,
      importedInvoiceEntries,
      importedInvoiceEntryMetadataMap,
    },
    { setImportedInvoicesStoreValue },
  ] = useImportedInvoicesStore();

  const selectedInvoice = importedInvoices.find(
    (invoice: ImportedInvoice) =>
      invoice.importedInvoiceId === selectedImportedInvoiceId
  );
  const visibleEntries = importedInvoiceEntries.filter(
    (entry: ImportedInvoiceEntry) =>
      importedInvoiceEntryMetadataMap[entry.importedInvoiceEntryId]?.visible ===
      true
  ).length;

  return (
    <ThemeProvider isDarkMode={appState.darkMode}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          padding: "6px 12px 6px 0",
          borderBottom: "1px solid rgba(128, 128, 128, 0.2)",
        }}
      >
        <Search
          dataHelp={helpAnchor("imported-invoices-entries-search")}
          placeholder={t("pct.searchImportedInvoiceEntries.label")}
          value={entriesSearchTerm}
          disabled={selectedImportedInvoiceId === null}
          onChange={(value) =>
            setImportedInvoicesStoreValue("entriesSearchTerm", value)
          }
        />
        {selectedInvoice ? (
          <span style={{ fontSize: 12 }}>
            {t("pct.importedInvoiceNumber.label")}:{" "}
            <b>{selectedInvoice.invoiceNumber}</b>
            {selectedInvoice.pendingBankStatementEntryId ? (
              <>
                {" | "}
                {t("pct.importedInvoicePendingBankStatementEntry.label")}:{" "}
                <b>{selectedInvoice.pendingBankStatementEntryId}</b>
              </>
            ) : null}
          </span>
        ) : null}
        <FlexRight>
          <Flex>
            {selectedImportedInvoiceId !== null ? (
              <ResultsScore
                score={visibleEntries}
                dataHelp={helpAnchor("imported-invoices-entries-count")}
              />
            ) : null}
          </Flex>
        </FlexRight>
      </div>
    </ThemeProvider>
  );
};

export default ImportedInvoiceEntriesSearchBar;
